import type { Breakpoint, Connection } from './connections';
import { anchorOf } from './connections';
import { jobHistory } from '../jobHistory';

/*
 * All measurements are taken from untransformed wrapper elements (see Reveal),
 * so offsets below are in layout coordinates and survive resizes.
 */

// ---------------------------------------------------------------------------
// Shared: two horizontal lines below the subtitle, grown from the center.
// Part of the header, so they keep their load animation on every breakpoint.
// ---------------------------------------------------------------------------
function headerLines(breakpoint: Breakpoint, style?: Connection['style']): Connection[] {
  const lines: Connection[] = [
    {
      id: `header-line-1-left-${breakpoint}`,
      from: { element: '.header-block', anchor: 'bottom', offset: { y: 13 } },
      to: { element: '.header-block', anchor: 'bottom-left', offset: { y: 13 } },
      animation: { delay: 0, duration: 1.5 },
    },
    {
      id: `header-line-1-right-${breakpoint}`,
      from: { element: '.header-block', anchor: 'bottom', offset: { y: 13 } },
      to: { element: '.header-block', anchor: 'bottom-right', offset: { y: 13 } },
      animation: { delay: 0, duration: 1.5 },
    },
    {
      id: `header-line-2-left-${breakpoint}`,
      from: { element: '.header-block', anchor: 'bottom', offset: { y: 20 } },
      to: { element: '.header-block', anchor: 'bottom-left', offset: { x: 40, y: 20 } },
      animation: { delay: 0.2, duration: 1.5 },
    },
    {
      id: `header-line-2-right-${breakpoint}`,
      from: { element: '.header-block', anchor: 'bottom', offset: { y: 20 } },
      to: { element: '.header-block', anchor: 'bottom-right', offset: { x: -40, y: 20 } },
      animation: { delay: 0.2, duration: 1.5 },
    },
  ];
  return lines.map((c) => ({ ...c, breakpoints: [breakpoint], style }));
}

// ---------------------------------------------------------------------------
// Desktop: spine runs down the column gap, branches out to each card.
// ---------------------------------------------------------------------------

/** Distance from the left column's right edge to the spine. */
const DESKTOP_SPINE_GAP = 37;

const desktopSpineX = () => {
  const col = anchorOf('.contact-section', 'right');
  return col ? col.x + DESKTOP_SPINE_GAP : null;
};

const desktopJobBranches: Connection[] = jobHistory.map((_, index) => ({
  id: `spine-to-job-${index}`,
  from: { element: `.job-${index}`, anchor: 'left', offset: { x: -43 } },
  to: { element: `.job-${index}`, anchor: 'left' },
  breakpoints: ['desktop'],
  animation: { delay: 1.4 + index * 0.2, duration: 0.8 },
}));

const desktopSectionBranches: Connection[] = ['contact', 'education', 'awards'].map(
  (section, index) => ({
    id: `spine-to-${section}`,
    from: { element: `.${section}-section`, anchor: 'right', offset: { x: DESKTOP_SPINE_GAP } },
    to: { element: `.${section}-section`, anchor: 'right' },
    breakpoints: ['desktop'],
    animation: { delay: 1.2 + index * 0.2, duration: 0.8 },
  })
);

const desktop: Connection[] = [
  ...headerLines('desktop'),
  {
    id: 'vertical-spine',
    from: () => {
      const x = desktopSpineX();
      const header = anchorOf('.header-block', 'bottom');
      return x !== null && header ? { x, y: header.y + 20 } : null;
    },
    to: () => {
      // End exactly at the lower of the two bottom-most branches:
      // awards (left side) vs the last job (right side)
      const x = desktopSpineX();
      const awards = anchorOf('.awards-section', 'right');
      const lastJob = anchorOf(`.job-${jobHistory.length - 1}`, 'left');
      if (x === null || !awards || !lastJob) return null;
      return { x, y: Math.max(awards.y, lastJob.y) };
    },
    breakpoints: ['desktop'],
    animation: { delay: 0.6, duration: 2 },
  },
  ...desktopSectionBranches,
  ...desktopJobBranches,
];

// ---------------------------------------------------------------------------
// Mobile: a single scroll-driven spine that starts under the photo, runs down
// a left rail past Contact/Education/Awards, jogs right under the Experience
// heading, and runs down a right rail to the last job. Branches draw when their
// section scrolls into view; the cards fade up once the branch has drawn.
// ---------------------------------------------------------------------------

/** Horizontal room reserved beside the content for the rail (App pads by this). */
export const RAIL_GUTTER = 48;
/** Rail distance from the outer content edge; GAP is rail -> content edge. */
const RAIL_INSET = 12;
const GAP = RAIL_GUTTER - RAIL_INSET;
/** Branches stop just short of the text. */
const BRANCH_PAD = 6;
/** How far the spine drops below the photo before jogging to the rail. */
const PHOTO_DROP = 20;
/** Jog line sits this far under the Experience heading. */
const JOG_OFFSET = 12;

const MOBILE_BRANCH_DURATION = 0.4;
/** Time (s) after mount before the spine starts drawing; header fades in first. */
const MOBILE_SPINE_DELAY = 0.6;

const rail = { color: '#3b82f6', width: 2, opacity: 0.6, nodeColor: '#60a5fa' };

const railLeftX = () => {
  const h = anchorOf('.contact-section .section-heading', 'left');
  return h ? h.x - GAP : null;
};
const railRightX = () => {
  const t = anchorOf('.job-0 .job-title', 'right');
  return t ? t.x + GAP : null;
};
const jogY = () => {
  const e = anchorOf('.experience-heading', 'bottom');
  return e ? e.y + JOG_OFFSET : null;
};

const mobileSectionBranches: Connection[] = ['contact', 'education', 'awards'].map(
  (section, index) => {
    const heading = `.${section}-section .section-heading`;
    return {
      id: `spine-to-${section}`,
      from: { element: heading, anchor: 'left', offset: { x: -GAP } },
      to: { element: heading, anchor: 'left', offset: { x: -BRANCH_PAD } },
      breakpoints: ['mobile'],
      trigger: 'in-view',
      node: 'start',
      inView: { watch: `.${section}-section` },
      // Earliest start after load, so above-the-fold branches wait for the spine
      animation: { delay: 1.0 + index * 0.15, duration: MOBILE_BRANCH_DURATION },
      style: rail,
    };
  }
);

const mobileJobBranches: Connection[] = jobHistory.map((_, index) => {
  const title = `.job-${index} .job-title`;
  return {
    id: `spine-to-job-${index}`,
    from: { element: title, anchor: 'right', offset: { x: GAP } },
    to: { element: title, anchor: 'right', offset: { x: BRANCH_PAD } },
    breakpoints: ['mobile'],
    trigger: 'in-view',
    node: 'start',
    inView: { watch: `.job-${index}` },
    animation: { delay: 1.4, duration: MOBILE_BRANCH_DURATION },
    style: rail,
  };
});

const mobile: Connection[] = [
  ...headerLines('mobile', { ...rail, opacity: 0.5 }),
  // Short drop from the lower header line to the top of the photo, so the
  // header lines, photo and spine read as one throughline.
  {
    id: 'header-to-photo',
    from: { element: '.header-block', anchor: 'bottom', offset: { y: 20 } },
    to: { element: '.profile-image', anchor: 'top' },
    breakpoints: ['mobile'],
    animation: { delay: 0.3, duration: 0.5 },
    style: rail,
  },
  {
    id: 'mobile-spine',
    trigger: 'scroll',
    from: { element: '.profile-image', anchor: 'bottom' },
    via: [
      () => {
        const p = anchorOf('.profile-image', 'bottom');
        return p ? { x: p.x, y: p.y + PHOTO_DROP } : null;
      },
      () => {
        const p = anchorOf('.profile-image', 'bottom');
        const x = railLeftX();
        return p && x !== null ? { x, y: p.y + PHOTO_DROP } : null;
      },
      () => {
        const x = railLeftX();
        const y = jogY();
        return x !== null && y !== null ? { x, y } : null;
      },
      () => {
        const x = railRightX();
        const y = jogY();
        return x !== null && y !== null ? { x, y } : null;
      },
    ],
    // Ends exactly on the last job's node; the tip fades out as it arrives.
    to: () => {
      const x = railRightX();
      const last = anchorOf(`.job-${jobHistory.length - 1} .job-title`, 'right');
      return x !== null && last ? { x, y: last.y } : null;
    },
    breakpoints: ['mobile'],
    scroll: { focus: 0.7 },
    animation: { delay: MOBILE_SPINE_DELAY },
    style: rail,
  },
  ...mobileSectionBranches,
  ...mobileJobBranches,
];

export const connections: Connection[] = [...desktop, ...mobile];
